import React, { useEffect, useMemo, useRef } from "react";
import "./RelatorioGuiaDosagemPrint.css";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function formatarCodigoDosagem(valor = "", numeroRevisao = "0") {
  const codigo = String(valor).trim().toUpperCase().match(/\d+(?:\.[A-Z0-9]{1,5})?/)?.[0] || "";
  const revisaoTexto = String(numeroRevisao ?? "");
  const revisao =
    revisaoTexto.match(/REV\.?\s*(\d+)/i)?.[1] ||
    revisaoTexto.replace(/\D/g, "") ||
    "0";

  return codigo ? `T${codigo}-R${revisao}` : "-";
}

function numero(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;
  const n = Number(String(valor).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function numeroBR(valor, casas = 0) {
  const n = typeof valor === "number" ? valor : numero(valor);
  if (n === null) return "-";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function guiaDosagemEhIndividual(dosagem) {
  const tipo = normalizarTexto(dosagem?.tipoGuiaDosagem || dosagem?.tipoGuia).replace(/\s+/g, "");
  return ["individual", "pesoindividual", "pesosindividuais"].includes(tipo);
}

function tituloTabelaPesagem(dados) {
  return dados.tipoGuiaIndividual
    ? "TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS INDIVIDUAIS, UM SILO PARA CADA AGREGADO)"
    : `TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS ACUMULADOS, ${dados.temGraudo ? "BRITA + AREIA" : "AREIA"})`;
}

function rotuloMaterialUmidade(rotulo) {
  const texto = String(rotulo || "").trim();
  const partes = texto.split(/\s+/);
  const primeiraPalavra = partes[0] || texto;
  const ehAreia = normalizarTexto(primeiraPalavra) === "areia";
  const indiceAreia = partes.findIndex((parte) => normalizarTexto(parte) === "areia");

  if (!ehAreia && indiceAreia > 0) {
    return (
      <span className="rotulo-material-umidade">
        <span>{partes.slice(0, indiceAreia).join(" ")}</span>
        <span className="rotulo-material-umidade-complemento">
          {partes.slice(indiceAreia).join(" ")}
        </span>
      </span>
    );
  }

  const palavrasVisiveis = ehAreia ? partes.slice(0, 2) : partes;
  const complemento = palavrasVisiveis.slice(1).join(" ");

  if (!ehAreia || !complemento) {
    return texto;
  }

  return (
    <span className="rotulo-material-umidade">
      <span>{palavrasVisiveis[0]}</span>
      <span className="rotulo-material-umidade-complemento">{complemento}</span>
    </span>
  );
}

function valorOuTraco(valor) {
  return valor === "" || valor === null || valor === undefined ? "-" : valor;
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.seconds) return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}

function fckPrincipal(valor) {
  const texto = valorOuTraco(valor);
  if (texto === "-") return texto;
  return String(texto).split("/")[0].trim();
}

function imprimir() {
  const conteudo = document.querySelector(
    ".relatorio-guia-dosagem-print:not(.relatorio-guia-dosagem-anexo)"
  );

  if (!conteudo) {
    alert("Não foi possível localizar a guia de dosagem para impressão.");
    return;
  }

  const estilos = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]')
  )
    .map((elemento) => elemento.outerHTML)
    .join("\n");

  const janela = window.open("", "_blank", "width=1200,height=800");

  if (!janela) {
    alert("Permita pop-ups para imprimir a guia de dosagem.");
    return;
  }

  janela.document.write(`
    <html>
      <head>
        <title>Guia de Dosagem</title>
        <base href="${window.location.origin}/" />
        ${estilos}
        <style>
          @page {
            size: A4 landscape;
            margin: 4mm;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #fff;
          }

          * {
            box-sizing: border-box;
          }

          .relatorio-guia-dosagem-print {
            background: #fff !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 289mm !important;
          }

          .relatorio-guia-dosagem-print .acoes-print {
            display: none !important;
          }

          .relatorio-guia-dosagem-print .guia-pagina {
            width: 289mm !important;
            min-height: 202mm !important;
            margin: 0 !important;
            padding: 3mm 4mm !important;
            background: #fff !important;
            overflow: hidden !important;
          }

          .relatorio-guia-dosagem-print * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        </style>
      </head>
      <body>
        ${conteudo.outerHTML}
        <script>
          window.onload = async function () {
            const imagens = Array.from(document.images);
            await Promise.all(imagens.map(function (imagem) {
              if (imagem.complete) return Promise.resolve();
              return new Promise(function (resolve) {
                imagem.onload = resolve;
                imagem.onerror = resolve;
              });
            }));
            window.focus();
            window.print();
            window.close();
          };
        </script>
      </body>
    </html>
  `);

  janela.document.close();
}

function nomeAgregado(material, fallback) {
  return (
    material?.descricao ||
    material?.Descricao ||
    material?.Descrição ||
    material?.descricaoMaterial ||
    material?.materialDescricao ||
    material?.material ||
    material?.amostra ||
    fallback
  );
}

function densidadeAgregado(material = {}) {
  return numero(
    material.densidade ||
      material.densidadeMedia ||
      material.massaEspecifica ||
      material.Densidade ||
      material.MassaEspecifica ||
      material.caracterizacaoCompleta?.densidade?.densidadeMedia ||
      material.caracterizacaoCompleta?.densidadeMedia ||
      material.caracterizacaoCompleta?.massaEspecifica
  );
}

function calcularDadosGuia(dosagem, materiais = []) {
  const tipoGuiaIndividual = guiaDosagemEhIndividual(dosagem);
  const cimento = dosagem?.cimentoResumo || dosagem?.cimento || {};
  const aditivos = Array.isArray(dosagem?.aditivosResumo) ? dosagem.aditivosResumo : [];
  const adicoes = Array.isArray(dosagem?.adicoesResumo)  ? dosagem.adicoesResumo  : Array.isArray(dosagem?.adicoes)
    ? dosagem.adicoes
    : [];
  const agregados = materiais;
  const graudos = agregados.filter((material) => material.tipoAgregado !== "miudo");
  const miudos = agregados.filter((material) => material.tipoAgregado === "miudo");
  const miudo = miudos[0] || {};
  const miudo2 = miudos[1] || {};
  const britas = graudos.slice(0, 2);
  const temGraudo = britas.length > 0;
  const brita1 = britas[0] || {};
  const brita2 = britas[1] || {};
  const temMiudo2 = miudos.length > 1;

  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const pesosAdicoes = adicoes.map((_, index) =>
  pesoCimento * ((numero(dosagem[`adicaoParcial${index}`]) || 0) / 100)
);
  const pesoLigante = pesoCimento + pesosAdicoes.reduce((total, peso) => total + peso, 0);
const fatorAc = numero(dosagem.fatorAc) || 0;
const pesoAgua = pesoLigante * fatorAc;
  const indiceMiudo = agregados.findIndex((material) => material.tipoAgregado === "miudo");
  const percentualMiudo = indiceMiudo >= 0 ? numero(dosagem[`materialParcial${indiceMiudo}`]) || 0 : 0;
  const percentualGraudo = graudos.length > 0 ? Math.max(0, 100 - percentualMiudo) : 0;
  const percentualGrupoAgregado = (material) =>
    material.tipoAgregado === "miudo" ? percentualMiudo : percentualGraudo;
  const somaProporcoesGrupo = (tipoAgregado) => agregados.reduce(
    (total, material, index) => material.tipoAgregado === tipoAgregado
      ? total + (numero(dosagem[`materialProporcaoGrupo${index}`]) || 0)
      : total,
    0
  );
  const percentualParcialAgregado = (material, index) => {
    const somaProporcoes = somaProporcoesGrupo(material.tipoAgregado);
    const proporcao = numero(dosagem[`materialProporcaoGrupo${index}`]) || 0;

    return somaProporcoes > 0
      ? percentualGrupoAgregado(material) * (proporcao / somaProporcoes)
      : 0;
  };
  const percentualAgregadoTotal = (index) =>
    percentualParcialAgregado(agregados[index], index) / 100;

  const volumeCimento = pesoCimento / (numero(dosagem.cimentoDensidade || cimento.massaEspecifica || "3,100") || 1);
const volumeAdicoes = adicoes.reduce((total, adicao, index) => {
  const massaEspecifica = numero(adicao.massaEspecifica || adicao.densidade) || 1;
  return total + pesosAdicoes[index] / massaEspecifica;
}, 0);
const volumeAgua = pesoAgua;
const volumeAditivos = aditivos.reduce((total, aditivo, index) => {
    const pesoAditivo = pesoLigante * ((numero(dosagem[`aditivoParcial${index}`]) || 0) / 100);
    return total + pesoAditivo / (numero(aditivo.massaEspecifica) || 1);
  }, 0);
  const volumeAr = (numero(dosagem.teorArConsiderado) ?? 0) * 10;
  const volumeAgregados = Math.max(
  0,
  1000 - volumeCimento - volumeAdicoes - volumeAgua - volumeAditivos - volumeAr
);
  const somaParticipacaoAgregados = agregados.reduce(
    (total, _, index) => total + percentualAgregadoTotal(index),
    0
  );
  const divisorVolumeAgregados = agregados.reduce((total, material, index) => {
    const participacao =
      somaParticipacaoAgregados > 0
        ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
        : 0;

    return total + participacao / (densidadeAgregado(material) || 1);
  }, 0);
  const massaAgregados = divisorVolumeAgregados > 0 ? volumeAgregados / divisorVolumeAgregados : 0;
  const pesoAgregado = (index) => {
    const participacao =
      somaParticipacaoAgregados > 0
        ? percentualAgregadoTotal(index) / somaParticipacaoAgregados
        : 0;

    return massaAgregados * participacao;
  };

  const pesoMiudo = indiceMiudo >= 0 ? pesoAgregado(indiceMiudo) : 0;
  const indiceMiudo2 = agregados.indexOf(miudo2);
const pesoMiudo2 = indiceMiudo2 >= 0 ? pesoAgregado(indiceMiudo2) : 0;
  const indiceBrita1 = agregados.indexOf(brita1);
  const indiceBrita2 = agregados.indexOf(brita2);
  const pesoBrita1 = indiceBrita1 >= 0 ? pesoAgregado(indiceBrita1) : 0;
  const pesoBrita2 = indiceBrita2 >= 0 ? pesoAgregado(indiceBrita2) : 0;
  const pesosBritas = britas.map((brita) => {
    const indice = agregados.indexOf(brita);
    return indice >= 0 ? pesoAgregado(indice) : 0;
  });
  const pesosAditivos = aditivos.map((_, index) =>
  pesoLigante * ((numero(dosagem[`aditivoParcial${index}`]) || 0) / 100)
);
  const volumesAditivos = aditivos.map((aditivo, index) => pesosAditivos[index] / (numero(aditivo.massaEspecifica) || 1));

  return {
  tipoGuiaIndividual,
  cimento,
  adicoes,
  aditivos,
  britas,
  temGraudo,
  miudo,
  miudo2,
  temMiudo2,
  brita1,
  brita2,
  pesoCimento,
  pesosAdicoes,
  pesoMiudo,
  pesoMiudo2,
  pesoBrita1,
  pesoBrita2,
  pesosBritas,
  pesoAgua,
  pesosAditivos,
  volumesAditivos,
};
}

function CelulaInfo({ titulo, children, valorAzul }) {
  return (
    <td className="guia-celula-material">
      <strong>{titulo}</strong>
      <br />
      <span className={valorAzul ? "valor-azul" : ""}>{children}</span>
    </td>
  );
}

function RotuloVertical({ titulo, subtitulo }) {
  return (
    <span className="texto-vertical">
      <span className="texto-vertical-linha">{titulo}</span>
      {subtitulo ? <span className="texto-vertical-linha">{subtitulo}</span> : null}
    </span>
  );
}

function tabelaPesagem(dados) {
  const volumes = Array.from({ length: 19 }, (_, index) => 1 + index * 0.5);
  const umidades = [2, 3, 4, 5, 6, 7, 8];

  return volumes.map((volume) => {
    let pesoBritasAcumulado = 0;
    const britasAcumuladas = Object.fromEntries(
      dados.pesosBritas.map((peso, index) => {
        pesoBritasAcumulado += peso * volume;

        return [
          `brita${index + 1}`,
          dados.tipoGuiaIndividual ? peso * volume : pesoBritasAcumulado,
        ];
      })
    );

    return {
      volume,
      constantes: {
        cimento: dados.pesoCimento * volume,
        adicao1: (dados.pesosAdicoes?.[0] || 0) * volume,
        ...britasAcumuladas,
        aditivo1: (dados.volumesAditivos[0] || 0) * volume,
        aditivo2: (dados.volumesAditivos[1] || 0) * volume,
        aditivo3: (dados.volumesAditivos[2] || 0) * volume,
      },
      umidades: umidades.map((umidade) => {
        const totalGraudos = dados.tipoGuiaIndividual ? 0 : pesoBritasAcumulado;
        const pesoMiudoUmido =
          dados.pesoMiudo * volume * (1 + umidade / 100);
        const pesoMiudo2Umido =
          (dados.pesoMiudo2 || 0) * volume * (1 + umidade / 100);
        const areia = totalGraudos + pesoMiudoUmido;
        const po = dados.temMiudo2
          ? dados.tipoGuiaIndividual
            ? pesoMiudo2Umido
            : totalGraudos + pesoMiudoUmido + pesoMiudo2Umido
          : 0;
        const correcaoAgua =
          ((dados.pesoMiudo || 0) + (dados.pesoMiudo2 || 0)) *
          volume *
          (umidade / 100);

        return {
          umidade,
          areia,
          po,
          agua: Math.max(0, dados.pesoAgua * volume - correcaoAgua),
        };
      }),
    };
  });
}

function colunasConstantes(dados) {
  const colunas = [
    { id: "cimento", grupo: "base", titulo: "CIMENTO", valor: (linha) => numeroBR(linha.constantes.cimento, 0) },
  ];

  if (dados.adicoes?.[0] && dados.pesosAdicoes?.[0]) {
  colunas.push({
    id: "adicao1",
    grupo: "base",
    titulo: "ADIÇÃO",
    subtitulo:
      dados.adicoes[0]?.descricao ||
      dados.adicoes[0]?.material ||
      "METACAULIM",
    valor: (linha) =>
      numeroBR(linha.constantes.adicao1, 0),
  });
}

  dados.britas.forEach((brita, index) => {
    const id = `brita${index + 1}`;
    const peso = dados.pesosBritas[index] || 0;

    if (!brita?.id || !peso) return;

    colunas.push({
      id,
      grupo: "base",
      titulo: nomeAgregado(brita, `BRITA ${index + 1}`),
      subtitulo: "",
      valor: (linha) => numeroBR(linha.constantes[id], 0),
    });
  });

  dados.aditivos.slice(0, 3).forEach((aditivo, index) => {
    colunas.push({
      id: `aditivo${index + 1}`,
      grupo: "aditivo",
      titulo: aditivo.descricao || `ADITIVO ${index + 1}`,
      valor: (linha) => numeroBR(linha.constantes[`aditivo${index + 1}`], 1),
    });
  });

  return colunas;
}

export default function RelatorioGuiaDosagemPrint({
  dosagem,
  materiais = [],
  autoPrint = false,
  onVoltar,
  embedded = false
}) {
  const jaImprimiuRef = useRef(false);
  useNomeArquivoPdf(formatarCodigoDosagem(dosagem?.codigo, dosagem?.numeroRevisao ?? dosagem?.traco));
  const dados = useMemo(() => calcularDadosGuia(dosagem || {}, materiais), [dosagem, materiais]);
  const linhas = useMemo(() => tabelaPesagem(dados), [dados]);
  const paginasLinhas = useMemo(
    () => [linhas.slice(0, 15), linhas.slice(15)].filter((pagina) => pagina.length > 0),
    [linhas]
  );
  const constantes = useMemo(() => colunasConstantes(dados), [dados]);
  const colunasBase = constantes.filter((coluna) => coluna.grupo !== "aditivo");
  const colunasAditivos = constantes.filter((coluna) => coluna.grupo === "aditivo");
  const totalConstantes = constantes.length;
  const rotuloAreia = dados.temMiudo2
    ? dados.tipoGuiaIndividual
      ? "AREIA 1"
      : dados.temGraudo
        ? "BRITA + AREIA 1"
        : "AREIA 1"
    : dados.tipoGuiaIndividual
      ? nomeAgregado(dados.miudo, "AREIA")
      : dados.temGraudo
        ? "BRITA + AREIA"
        : "AREIA";

  const rotuloPoVisual = dados.temMiudo2
    ? dados.tipoGuiaIndividual
      ? "AREIA 2"
      : dados.temGraudo
        ? "BRITA + AREIA 1 E 2"
        : "AREIA 1 E 2"
    : "-";

  useEffect(() => {
    if (embedded || !autoPrint || jaImprimiuRef.current) return;
    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(imprimir);
    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint]);

const itensResumoGuia = [
  {
  titulo: "Cimento",
  valor: `${numeroBR(dados.pesoCimento, 0)} kg/m³`,
    descricao: dados.cimento.descricao || "-",
    fornecedor: dados.cimento.fornecedor || "-",
  },
  ...dados.adicoes.map((adicao, index) => ({
    titulo: index === 0 ? "Adição" : `Adição ${index + 1}`,
   valor: dados.pesosAdicoes?.[index]
  ? `${numeroBR(dados.pesosAdicoes[index], 0)} kg/m³`
  : "-",
    descricao: adicao.descricao || adicao.material || "-",
    fornecedor: adicao.fornecedor || "-",
  })),
  ...materiais.map((material) => {
  const indiceBrita = dados.britas.findIndex((brita) => brita.id === material.id);
  const indiceMiudo = [dados.miudo, dados.miudo2].findIndex(
  (miudo) => miudo === material || (miudo?.id && miudo.id === material.id)
);
const nomeMaterial = nomeAgregado(
  material,
  material.tipoAgregado === "miudo" ? "Areia" : `Brita ${indiceBrita + 1}`
);

const pesoMaterial =
  material.tipoAgregado === "miudo"
    ? indiceMiudo === 0
      ? dados.pesoMiudo
      : indiceMiudo === 1
        ? dados.pesoMiudo2
        : 0
    : indiceBrita === 0
      ? dados.pesoBrita1
      : indiceBrita === 1
        ? dados.pesoBrita2
        : 0;

  return {
    titulo: material.tipoAgregado === "miudo" && dados.temMiudo2 && indiceMiudo >= 0
      ? `AREIA ${indiceMiudo + 1} - ${nomeMaterial}`
      : nomeMaterial,
    valor: pesoMaterial ? `${numeroBR(pesoMaterial, 0)} kg/m³` : "-",
    descricao: material.procedencia || "-",
    fornecedor: material.localColeta || "-",
  };
}),
  ...dados.aditivos.slice(0, 2).map((aditivo, index) => ({
    titulo: `Aditivo ${index + 1}`,
    valor: `${numeroBR(dados.volumesAditivos[index], 1)} l/m³`,
    descricao: aditivo.descricao || "-",
    fornecedor: aditivo.fornecedor || "-",
  })),
  {
  titulo: "Água",
  valor: `${numeroBR(dados.pesoAgua, 0)} l/m³`,
    descricao: "",
    fornecedor: "",
  },
];

const linhasResumoGuia = (() => {
  const totalLinhas = Math.max(1, Math.ceil(itensResumoGuia.length / 6));
  const itensPorLinha = Math.ceil(itensResumoGuia.length / totalLinhas);

  return Array.from({ length: totalLinhas }, (_, indiceLinha) =>
    itensResumoGuia.slice(
      indiceLinha * itensPorLinha,
      (indiceLinha + 1) * itensPorLinha
    )
  ).filter((linha) => linha.length > 0);
})();


  return (
    <div className={`relatorio-guia-dosagem-print${embedded ? " relatorio-guia-dosagem-anexo" : ""}`}>
      {!embedded && (
        <div className="acoes-print">
          {onVoltar && <button className="btn-voltar" onClick={onVoltar}>Voltar</button>}
          <button className="btn-imprimir" onClick={imprimir}>Imprimir</button>
        </div>
      )}

      {paginasLinhas.map((linhasPagina, indicePagina) => (
      <div
        className="guia-pagina"
        key={`guia-pagina-${indicePagina}`}
        style={indicePagina > 0 ? { breakBefore: "page", pageBreakBefore: "always" } : undefined}
      >
        <table className="guia-cabecalho">
          <tbody>
            <tr>
              <td rowSpan="3" className="guia-logo"><LogoRelatorio alt="Logo"  /></td>
              <td rowSpan="3" className="guia-titulo">
                <div>SISTEMA DE GESTÃO DA QUALIDADE</div>
                <div>Concreto - Laudo de dosagem - Carta traço</div>
              </td>
              <td>RQ 11.1</td>
            </tr>
            <tr><td>REVISÃO 00</td></tr>
            <tr><td>20/08/2025</td></tr>
          </tbody>
        </table>

        <table className="guia-data-codigo">
          <tbody>
            <tr>
              <td>DATA: <strong>{formatarDataBR(dosagem.data)}</strong></td>
              <td>
                CÓDIGO:{" "}
                <strong>
                  {formatarCodigoDosagem(
                    dosagem.codigo,
                    dosagem.numeroRevisao ?? dosagem.traco
                  )}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="guia-empresa">
          <div><strong>EMPRESA:</strong> {dosagem.clienteNome || "-"}</div>
          <div><strong>OBRA:</strong> {dosagem.obraNome || "-"}</div>
        </div>

        <div className="guia-faixa">GUIA DE DOSAGEM DE CONCRETO</div>
        <div className="guia-resumo">
          <span>TRAÇO Nº: <strong>{dosagem.traco || "-"}</strong></span>
          <span>FCK: <strong>{fckPrincipal(dosagem.fck)} MPa</strong></span>
          <span>FATOR A/C: <strong>{valorOuTraco(dosagem.fatorAc)}</strong></span>
          <span>
            SLUMP:{" "}
            <strong>
              {dosagem.slump
                ? `${dosagem.slump}${dosagem.toleranciaSlumpProjeto ? ` ± ${dosagem.toleranciaSlumpProjeto}` : ""} mm`
                : "-"}
            </strong>
          </span>
          <span>Data da execução do traço: <strong>{formatarDataBR(dosagem.data)}</strong></span>
        </div>

        <div className="guia-subtitulo">DADOS DO TRAÇO - PARA CÁLCULO DA TABELA</div>
        <div className="guia-dados-traco-grid">
          {linhasResumoGuia.map((linha, indiceLinha) => (
            <div
              className="guia-dados-traco-linha"
              key={`linha-${indiceLinha}`}
              style={{
                gridTemplateColumns: `repeat(${linha.length}, minmax(0, 1fr))`,
              }}
            >
              {linha.map((item, index) => (
                <div
                  className="guia-dados-traco-card"
                  key={`${item.titulo}-${indiceLinha}-${index}`}
                >
                  <div className="guia-card-esquerda">
                    <strong>{item.titulo}</strong>
                    <span>{item.valor}</span>
                  </div>

                  <div className="guia-card-direita">
                    <strong>{item.descricao || "-"}</strong>
                    {item.fornecedor ? <span>{item.fornecedor}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="guia-subtitulo">{tituloTabelaPesagem(dados)}</div>
        <table className="guia-tabela-pesagem">
          <colgroup>
            <col className="col-volume" />
            {constantes.map((coluna) => <col className="col-constante" key={`col-${coluna.id}`} />)}
            {Array.from({ length: 21 }, (_, index) => <col className="col-variavel" key={`col-var-${index}`} />)}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan="4" className="volume-title">
                <span className="texto-vertical">VOLUME A DOSAR</span>
              </th>
              <th colSpan={totalConstantes}>QUANT. CONSTANTES</th>
              <th colSpan="21">QUANTIDADES VARIÁVEIS COM A UMIDADE</th>
            </tr>
            <tr>
              {colunasBase.map((coluna) => (
                <th rowSpan="3" className="constante-vertical" key={coluna.id}>
                  <RotuloVertical titulo={coluna.titulo} subtitulo={coluna.subtitulo} />
                </th>
              ))}
              {colunasAditivos.length ? <th colSpan={colunasAditivos.length}>ADITIVO</th> : null}
              <th colSpan="21">UMIDADE DAS AREIAS</th>
            </tr>
            <tr>
              {colunasAditivos.map((coluna) => (
                <th rowSpan="2" className="constante-vertical constante-aditivo" key={coluna.id}>
                  <RotuloVertical titulo={coluna.titulo} />
                </th>
              ))}
              {[2, 3, 4, 5, 6, 7, 8].map((umidade) => <th colSpan="3" key={umidade}>{umidade}%</th>)}
            </tr>
            <tr>
              {[2, 3, 4, 5, 6, 7, 8].flatMap((umidade) => [
                <th key={`${umidade}-areia`}>{rotuloMaterialUmidade(rotuloAreia)}</th>,
                <th key={`${umidade}-po`}>{rotuloMaterialUmidade(rotuloPoVisual)}</th>,
                <th key={`${umidade}-agua`}>ÁGUA</th>
              ])}
            </tr>
          </thead>
          <tbody>
            {linhasPagina.map((linha) => (
              <tr key={linha.volume}>
                <td className="volume">{numeroBR(linha.volume, 1)}</td>
                {constantes.map((coluna) => <td key={`${linha.volume}-${coluna.id}`}>{coluna.valor(linha)}</td>)}
                {linha.umidades.flatMap((umidade) => [
                  <td key={`${linha.volume}-${umidade.umidade}-areia`}>{numeroBR(umidade.areia, 0)}</td>,
                 <td key={`${linha.volume}-${umidade.umidade}-po`}>
                  {umidade.po ? numeroBR(umidade.po, 0) : "-"}
                </td>,
                  <td className="agua" key={`${linha.volume}-${umidade.umidade}-agua`}>{numeroBR(umidade.agua, 0)}</td>,
                ])}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="guia-obs">
          Obs.: Atentar para a umidade correta da areia. Recomenda-se a determinação sempre no início dos trabalhos ou quando houver variação devido a chegada de material ou por chuva.<br />
          {dados.temGraudo
            ? dados.tipoGuiaIndividual
              ? "As massas dos agregados estão em pesos individuais. Não foi considerada umidade para a brita."
              : "As massas das areias e britas estão acumuladas. Não foi considerada umidade para a brita."
            : dados.tipoGuiaIndividual
              ? "As massas dos agregados estão em pesos individuais."
              : "As massas das areias estão acumuladas."}
        </div>
      </div>
      ))}
    </div>
  );
}
